import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  Box,
  Paper,
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

// Reuse the same status set/colors as PackageDashboard so badges look consistent
const STATUS_CHIP_STYLES = {
  delivered: { bgcolor: '#dcfce7', color: '#166534' },
  'in-transit': { bgcolor: '#dbeafe', color: '#1e40af' },
  pending: { bgcolor: '#f3f4f6', color: '#1f2937' },
  returned: { bgcolor: '#fef9c3', color: '#9a3412' },
  'in-store': { bgcolor: '#f3f4f6', color: '#1f2937' },
  canceled: { bgcolor: '#fee2e2', color: '#991b1b' },
  'left-store': { bgcolor: '#dbeafe', color: '#1e40af' },
  paid: { bgcolor: '#f3e8ff', color: '#6b21a8' },
  'returning-to-store': { bgcolor: '#fef9c3', color: '#854d0e' },
};

const StatusChip = ({ status }) => {
  const key = (status || 'pending').toLowerCase();
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending';
  return (
    <Chip
      label={label}
      size="small"
      sx={{ fontWeight: 600, ...(STATUS_CHIP_STYLES[key] || STATUS_CHIP_STYLES.pending) }}
    />
  );
};

const UserPackageDetail = () => {
  // Route param is the staff member's name - staff share a central login and tag
  // themselves on Add Package, so packages carry the name in createdBy, not a uid.
  const { staffName: encodedStaffName } = useParams();
  const staffName = decodeURIComponent(encodedStaffName || '');
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(searchParams.get('start') || '');
  const [endDate, setEndDate] = useState(searchParams.get('end') || '');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [authUser] = useAuthState(auth);
  const navigate = useNavigate();

  // Fetch packages created by this staff member
  useEffect(() => {
    const fetchPackages = async () => {
      if (!authUser || !staffName) return;
      try {
        const snapshot = await getDocs(collection(db, 'packages'));
        const pkgList = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(pkg => pkg.createdBy === staffName);
        setPackages(pkgList);
      } catch (error) {
        console.error('Error fetching packages:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, [authUser, staffName]);

  // Keep the URL query string in sync with the date filters
  useEffect(() => {
    const params = {};
    if (startDate) params.start = startDate;
    if (endDate) params.end = endDate;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const filteredPackages = packages.filter(pkg => {
    if (!pkg.createdAt?.seconds) return !startDate && !endDate;
    const pkgDate = new Date(pkg.createdAt.seconds * 1000);

    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      if (pkgDate < start) return false;
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      if (pkgDate > end) return false;
    }
    return true;
  });

  const sortedPackages = [...filteredPackages].sort((a, b) => {
    let aVal, bVal;
    switch (sortBy) {
      case 'recipient':
        aVal = a.recipient || '';
        bVal = b.recipient || '';
        break;
      case 'destination':
        aVal = a.destination || '';
        bVal = b.destination || '';
        break;
      case 'company':
        aVal = a.company || '';
        bVal = b.company || '';
        break;
      case 'status':
        aVal = a.status || '';
        bVal = b.status || '';
        break;
      case 'trackingNumber':
        aVal = a.trackingNumber || '';
        bVal = b.trackingNumber || '';
        break;
      case 'createdAt':
      default:
        aVal = a.createdAt?.seconds || 0;
        bVal = b.createdAt?.seconds || 0;
        break;
    }
    if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Route back to the package's own detail page, same as PackageDashboard
  const handleViewPackage = (packageId) => {
    navigate(`/package/${packageId}`);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  const displayName = staffName || 'this staff member';

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1280, xl: 1600 }, p: { xs: 2, md: 3 }, px: { lg: 4 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: { xs: 2, md: 3 } }}
        >
          <Box>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<ArrowBackIcon fontSize="small" />}
              onClick={() => navigate('/userpackagesummary')}
              sx={{ mb: 1 }}
            >
              Back to all staff
            </Button>
            <Typography variant="h5" fontWeight={600} color="text.primary">
              Packages created by {displayName}
            </Typography>
          </Box>
        </Stack>

        {/* Date filters */}
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
            <TextField
              label="From date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="To date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
            />
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear
            </Button>
          </Stack>
        </Paper>

        {/* Packages table */}
        {loading ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress size={22} sx={{ mr: 1.5, verticalAlign: 'middle' }} />
            <Typography component="span" color="text.secondary">Loading packages...</Typography>
          </Paper>
        ) : sortedPackages.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No packages found for this staff member in the selected range.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600 } }}>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'createdAt'}
                      direction={sortBy === 'createdAt' ? sortOrder : 'desc'}
                      onClick={() => handleSort('createdAt')}
                    >
                      Date Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'recipient'}
                      direction={sortBy === 'recipient' ? sortOrder : 'desc'}
                      onClick={() => handleSort('recipient')}
                    >
                      Recipient
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'company'}
                      direction={sortBy === 'company' ? sortOrder : 'desc'}
                      onClick={() => handleSort('company')}
                    >
                      Company
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'destination'}
                      direction={sortBy === 'destination' ? sortOrder : 'desc'}
                      onClick={() => handleSort('destination')}
                    >
                      Destination
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortOrder : 'desc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'trackingNumber'}
                      direction={sortBy === 'trackingNumber' ? sortOrder : 'desc'}
                      onClick={() => handleSort('trackingNumber')}
                    >
                      Tracking #
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPackages.map((pkg) => (
                  <TableRow
                    key={pkg.id}
                    hover
                    onClick={() => handleViewPackage(pkg.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="text.primary">
                        {pkg.createdAt?.seconds
                          ? new Date(pkg.createdAt.seconds * 1000).toLocaleString()
                          : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {pkg.recipient || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {pkg.company || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {pkg.destination || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={pkg.status} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {pkg.trackingNumber || 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 2 }}>
          Showing {sortedPackages.length} of {packages.length} packages
          {(startDate || endDate) && ' (filtered by date range)'}
        </Typography>
      </Box>
    </Box>
  );
};

export default UserPackageDetail;
